import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Pencil, Eye, EyeOff } from 'lucide-react';
import {
  adminListDraws,
  adminUpdateDrawWitness,
  adminToggleDrawVisibility,
} from '@/lib/api';
import { formatDateShort } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';

/**
 * ADMIN · DRAWS
 *
 * Audit-side management of every draw that has ever been executed on the
 * platform. The main surface is a one-row-per-draw table with the public
 * audit values on the left (seed excerpt, ticket #, drawn_at, witness
 * state) and a "Record witness" action per row.
 *
 * Witness editing posts to PATCH /api/admin/draws/{id}/witness which only
 * mutates the witnessed / witness_name / witness_role / witness_recorded_*
 * fields — the seed, eligible-ticket list and winner ticket are never
 * touched, so the public verification page stays mathematically re-runnable.
 *
 * The admin can also toggle a draw's visibility on the public /past-draws
 * page (same button as the existing Winners-side flow).
 */
const AdminDraws = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // the draw row we're editing

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListDraws();
      setItems(d.items || []);
    } catch {
      toast.error('Could not load draws');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleVisibility = async (draw) => {
    const nextHidden = !draw.hidden_from_public;
    const label = nextHidden ? 'Hide from public?' : 'Publish to public?';
    if (!window.confirm(label)) return;
    try {
      await adminToggleDrawVisibility(draw.id, nextHidden);
      toast.success(nextHidden ? 'Draw hidden from public' : 'Draw published');
      load();
    } catch {
      toast.error('Visibility change failed');
    }
  };

  const shortSeed = (s) => (s && String(s).length > 12 ? `${String(s).slice(0, 10)}…` : s || '—');

  return (
    <div data-testid="admin-draws-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl">Draws</h1>
          <p className="mt-2 text-sm text-muted-sage max-w-2xl">
            Every draw ever executed, newest first. Use <span className="text-[color:var(--pd-text)]">Record witness</span> to add
            the independent-witness confirmation on a draw retroactively — this only writes to the witness fields; the seed,
            eligible-ticket list and winning ticket are never changed.
          </p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-pd overflow-hidden bg-surface" data-testid="admin-draws-table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--pd-bg)] text-left">
            <tr className="text-[11px] uppercase tracking-widest text-muted-sage">
              <th className="px-4 py-3">Competition</th>
              <th className="px-4 py-3">Drawn</th>
              <th className="px-4 py-3 text-right">Entries</th>
              <th className="px-4 py-3">Seed</th>
              <th className="px-4 py-3">Winning #</th>
              <th className="px-4 py-3">Witness</th>
              <th className="px-4 py-3">Public</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--pd-border)]">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-muted-sage">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-muted-sage">
                  No draws yet.
                </td>
              </tr>
            )}
            {items.map((d) => (
              <tr key={d.id} data-testid={`admin-draw-row-${d.id}`}>
                <td className="px-4 py-3 text-[color:var(--pd-text)]">{d.competition_title || '—'}</td>
                <td className="px-4 py-3 text-muted-sage">{formatDateShort(d.drawn_at)}</td>
                <td className="px-4 py-3 text-right font-mono">
                  {(d.eligible_count || 0).toLocaleString('en-GB')}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-sage" title={String(d.seed ?? '—')}>
                  {shortSeed(d.seed)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[color:var(--pd-text)]">
                  {d.winner_ticket_number ?? '—'}
                </td>
                <td className="px-4 py-3">
                  {d.witnessed ? (
                    <span className="inline-flex items-center gap-1.5 text-[color:var(--pd-text)]">
                      <CheckCircle2 size={14} className="text-[color:var(--pd-gold)]" />
                      <span>{d.witness_name || 'Independently witnessed'}
                        {d.witness_role ? <span className="text-muted-sage">, {d.witness_role}</span> : null}
                      </span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[color:var(--pd-danger)]">
                      <AlertTriangle size={14} />
                      <span>Not recorded</span>
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleVisibility(d)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-sage hover:text-[color:var(--pd-text)]"
                    data-testid={`admin-draw-visibility-${d.id}`}
                    title={d.hidden_from_public ? 'Click to publish' : 'Click to hide'}
                  >
                    {d.hidden_from_public ? (
                      <><EyeOff size={14} /> Hidden</>
                    ) : (
                      <><Eye size={14} /> Public</>
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setEditing(d)}
                    className="inline-flex items-center gap-1.5 text-xs border border-pd rounded-lg px-3 py-1.5 hover:border-[color:var(--pd-accent-yellow)] hover:text-[color:var(--pd-accent-yellow)]"
                    data-testid={`admin-draw-record-witness-${d.id}`}
                  >
                    <Pencil size={12} /> Record witness
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <WitnessDialog
        draw={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </div>
  );
};

/**
 * Lightweight witness-edit dialog. Pre-filled from the draw row so an admin
 * can amend an existing entry. The "Independently witnessed" switch is the
 * authoritative control: switching it OFF wipes the name + role on save.
 */
const WitnessDialog = ({ draw, onClose, onSaved }) => {
  const [witnessed, setWitnessed] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!draw) return;
    setWitnessed(!!draw.witnessed);
    setName(draw.witness_name || '');
    setRole(draw.witness_role || '');
  }, [draw]);

  if (!draw) return null;

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateDrawWitness(draw.id, {
        witnessed,
        witness_name: witnessed ? name.trim() || null : null,
        witness_role: witnessed ? role.trim() || null : null,
      });
      toast.success(
        witnessed
          ? name.trim()
            ? 'Witness recorded on draw'
            : 'Draw marked as independently witnessed'
          : 'Witness record cleared'
      );
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not update witness');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!draw} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="witness-dialog">
        <DialogHeader>
          <DialogTitle>Record independent witness</DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-5">
          <div className="rounded-xl border border-pd bg-[color:var(--pd-bg)]/40 px-3 py-2 text-xs text-muted-sage">
            <div className="text-[color:var(--pd-text)]">{draw.competition_title}</div>
            <div className="mt-1 font-mono">Winning ticket #{draw.winner_ticket_number ?? '—'}</div>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm text-[color:var(--pd-text)]">Independently witnessed</div>
              <div className="text-xs text-muted-sage mt-0.5">
                Confirms a person outside The Padel Draw observed this draw.
              </div>
            </div>
            <Switch
              checked={witnessed}
              onCheckedChange={setWitnessed}
              data-testid="witness-toggle"
            />
          </div>

          <div className="space-y-3" aria-disabled={!witnessed}>
            <div>
              <label className="text-xs text-muted-sage">Witness name (optional)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!witnessed}
                placeholder="Leave blank to stay anonymous"
                data-testid="witness-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-sage">Witness role (optional)</label>
              <Input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={!witnessed}
                placeholder="e.g. Club manager, Solicitor"
                data-testid="witness-role-input"
              />
            </div>
            <p className="text-xs text-muted-sage">
              If the toggle is on and no name is provided, the public page will show
              <span className="text-[color:var(--pd-text)]"> &ldquo;Independently witnessed&rdquo;</span> without a name.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="witness-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="witness-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDraws;
